import { useState, useRef, useCallback, useEffect } from 'react';
import useFacebookAccounts from '../../../hooks/useFacebookAccounts';

/**
 * Custom hook to manage filters for campaigns and platforms
 */
const useFilters = () => {
  const [campaignFilter, setCampaignFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [facebookAccountId, setFacebookAccountId] = useState('');
  const [groupBy, setGroupBy] = useState(['subid_2']);
  const [searchText, setSearchText] = useState('');
  
  // Use a ref to track the current view mode
  const viewModeRef = useRef('subid2');
  
  // Get Facebook accounts from centralized configuration
  const { accounts: facebookAccounts } = useFacebookAccounts('analytics');
  
  // Set default Facebook account when accounts are loaded
  useEffect(() => {
    if (facebookAccounts.length > 0 && !facebookAccountId) {
      setFacebookAccountId(facebookAccounts[0].id);
    }
  }, [facebookAccounts, facebookAccountId]);
  
  // Handle campaign filter change - this is for server-side filtering
  const handleCampaignFilterChange = (e) => {
    setCampaignFilter(e.target.value);
    // Also update search text for client-side filtering
    setSearchText(e.target.value);
  };
  
  // Handle platform filter change  
  const handlePlatformFilterChange = (e) => {
    setPlatformFilter(e.target.value);
  };
  
  // Handle Facebook account filter change
  const handleFacebookAccountChange = useCallback((e) => {
    const newAccountId = e.target.value;
    console.log(`Facebook account changed from ${facebookAccountId} to ${newAccountId}`);
    setFacebookAccountId(newAccountId);
  }, [facebookAccountId]);
  
  // Check if Facebook account is required and selected
  const isFacebookAccountRequired = () => {
    return false; // No longer require account selection - we'll load all accounts
  };
  
  const isFacebookAccountSelected = () => {
    return true; // Always return true since we're loading all accounts
  };
  
  // Validate filters before search
  const validateFilters = () => {
    // No validation needed anymore since we're loading all accounts
    return { isValid: true };
  };
  
  // Reset all filters
  const resetFilters = () => {
    setCampaignFilter('');
    setPlatformFilter('all');
    setFacebookAccountId('');
    setSearchText('');
  };
  
  // Change grouping based on view
  const setSubid2GroupBy = () => {
    console.log('Switching to subid2 group by mode');
    viewModeRef.current = 'subid2';
    setGroupBy(['subid_2']);
  };
  
  const setSubid3GroupBy = () => {
    console.log('Switching to subid3 group by mode');
    viewModeRef.current = 'subid3';
    setGroupBy(['subid_3']);
  };
  
  const setSubid3And4GroupBy = () => {
    console.log('Switching to subid3 and subid4 group by mode');
    viewModeRef.current = 'subid3and4';
    setGroupBy(['subid_3', 'subid_4']);
  };
  
  const setSubid4GroupBy = () => {
    console.log('Switching to subid4 group by mode');
    viewModeRef.current = 'subid4';
    setGroupBy(['subid_4']);
  };
  
  // Filter campaigns based on search text
  const filterCampaignsBySearch = (campaigns) => {
    if (!searchText || !campaigns) return campaigns;
    
    const searchTextLower = searchText.toLowerCase();
    return campaigns.filter(campaign => {
      // Search in campaign name
      const campaignName = campaign.campaign_name || '';
      if (campaignName.toLowerCase().includes(searchTextLower)) return true;
      
      // Also search in campaign ID as fallback
      const campaignId = campaign.campaign_id || '';
      if (campaignId.toLowerCase().includes(searchTextLower)) return true;
      
      return false;
    });
  };
  
  // Get current filter state for API calls
  const getFilterState = () => {
    const state = {
      campaignFilter,
      platformFilter,
      facebookAccountId,
      groupBy,
      viewMode: viewModeRef.current
    };
    
    console.log('Current filter state:', state);
    return state;
  };
  
  return {
    campaignFilter,
    platformFilter,
    facebookAccountId,
    groupBy,
    searchText,
    facebookAccounts,
    handleCampaignFilterChange,
    handlePlatformFilterChange,
    handleFacebookAccountChange,
    isFacebookAccountRequired,
    isFacebookAccountSelected,
    validateFilters,
    resetFilters,
    setSubid2GroupBy,
    setSubid3GroupBy,
    setSubid3And4GroupBy,
    setSubid4GroupBy,
    setGroupBy,
    filterCampaignsBySearch,
    getFilterState,
    get viewMode() { return viewModeRef.current; }
  };
};

export default useFilters; 